import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  BarChart2,
  Settings,
  LogOut,
  PanelLeftClose,
  PanelLeftOpen,
  Book
} from 'lucide-react';
import { Logo } from './Logo';
import { useSidebarState } from '../hooks/useSidebarState';
import { useFirebaseAuth } from '../hooks/useFirebaseAuth';
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { useToast } from "@/components/ui/use-toast";

export function Sidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { isCollapsed, toggleSidebar } = useSidebarState();
  const { logout } = useFirebaseAuth();
  const { toast } = useToast();
  
  const menuItems = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/' },
    { icon: Users, label: 'Lista de Alunos', path: '/students' },
    { icon: BarChart2, label: 'Relatórios', path: '/open-rate-report' },
    { icon: Settings, label: 'Configurações', path: '/settings' },
    { icon: Book, label: 'Documentação', path: '/documentation' },
  ];

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
      toast({
        title: "Logout realizado",
        description: "Você foi desconectado com sucesso.",
      });
    } catch (error) {
      console.error('Erro ao fazer logout:', error);
      toast({
        variant: "destructive",
        title: "Erro ao fazer logout",
        description: "Não foi possível desconectar. Tente novamente.",
      });
    }
  };

  return (
    <div 
      className={`${
        isCollapsed ? 'w-[60px]' : 'w-64'
      } bg-card h-screen fixed left-0 top-0 border-r border-border transition-all duration-300 flex flex-col`}
    >
      {/* Logo */}
      <div className="p-4 border-b border-border">
        <div className={`transition-all duration-300 ${isCollapsed ? 'w-0 opacity-0' : 'w-auto opacity-100'}`}>
          <Logo className="h-8 w-auto" />
        </div>
      </div>

      {/* Menu */}
      <div className={`flex-1 p-3 ${isCollapsed ? 'overflow-hidden' : 'overflow-y-auto'}`}>
        <nav className="space-y-1">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            
            return (
              <Button
                key={item.path}
                variant={isActive ? "default" : "ghost"}
                className={`w-full justify-start ${isCollapsed ? 'px-3' : ''}`}
                asChild
              >
                <Link
                  to={item.path}
                  title={isCollapsed ? item.label : undefined}
                >
                  <Icon className="w-5 h-5 flex-shrink-0" />
                  <span className={`transition-all duration-300 ml-3 ${
                    isCollapsed ? 'w-0 opacity-0' : 'w-auto opacity-100'
                  } whitespace-nowrap`}>
                    {item.label}
                  </span>
                </Link>
              </Button>
            );
          })}
        </nav>
      </div>

      {/* Footer */}
      <div className="p-3 border-t border-border">
        <Button
          variant="destructive"
          onClick={handleLogout}
          className={`w-full justify-start ${isCollapsed ? 'px-3' : ''}`}
        >
          <LogOut className="w-5 h-5" />
          <span className={`transition-all duration-300 ml-3 ${
            isCollapsed ? 'w-0 opacity-0' : 'w-auto opacity-100'
          }`}>
            Sair
          </span>
        </Button>

        <Separator className="my-3" />

        <Button
          variant="outline"
          onClick={toggleSidebar}
          className={`w-full justify-start ${isCollapsed ? 'px-3' : ''}`}
          title={isCollapsed ? 'Expandir menu' : 'Recolher menu'}
        >
          {isCollapsed ? (
            <PanelLeftOpen className="w-5 h-5" />
          ) : (
            <>
              <PanelLeftClose className="w-5 h-5" />
              <span className="ml-3">Recolher menu</span>
            </>
          )}
        </Button>

        {/* Versão */}
        {!isCollapsed && (
          <div className="mt-4 text-center text-muted-foreground text-xs">
            <p>Versão 1.0.0</p>
          </div>
        )}
      </div>
    </div>
  );
}